import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CopyableCell } from '../../components/ui/CopyableCell';
import { effectiveMetaAdType } from '../../lib/effectiveMetaAdType';
import { renderRowTemplate, renderRowTemplateArray, type RowRenderContext } from '../../lib/rowContent';
import { resolveLeadForm } from '../../lib/resolveLeadForm';
import type { AccountCampaign } from '../../types/accountCampaign';
import type { Campaign, CampaignLeadForm, LeadFormText, MetaAdCopy } from '../../types/campaign';
import type { FormType, FranchiseLocationProfile } from '../../types/franchiseLocationProfile';
import { patchRow } from './api';
import { BuildViewField, BuildViewSection } from './BuildViewSection';
import { LeadFormCell } from './LeadFormCell';
import {
  DriftDot,
  type BuildViewDriftFlags,
} from './BuildViewModificationBadges';
import { CopyAllButton, VariationsListInline } from './VariationsListInline';

// P2b-3 Build View commit 4: Meta-specific content blocks.
//
// Order per spec §2.7:
//   - Body Copy (variations cards)
//   - Headlines (variations cards)
//   - Descriptions (variations cards)
//   - CTA (single field with copy)
//   - Lead Form (only when effective ad type is lead_gen)
//
// Every authored string passes through renderRowTemplate so the
// per-location placeholders ({suburb}, {gymInfo}, {monthYear}, …)
// substitute consistently with what the buyer sees on the role grid.
//
// Modification dots (per-section content drift indicators) land in
// commit 6 via the section's `badge` slot.

type Props = {
  // Multi Lead Form spec §3.4: rolloutId + archived flow in so the
  // Lead Form section can offer an inline reassign affordance when
  // the rollout isn't archived.
  rolloutId: string;
  archived: boolean;
  row: AccountCampaign;
  campaign: Campaign | null;
  locationProfile: FranchiseLocationProfile | null;
  renderCtx: RowRenderContext;
  drift: BuildViewDriftFlags;
};

export function BuildViewMetaContent({
  rolloutId,
  archived,
  row,
  campaign,
  locationProfile,
  renderCtx,
  drift,
}: Props) {
  // Multi Lead Form spec §3.4. Inline reassign uses the same patchRow
  // mutation the master grid runs against. The server handles
  // leadFormId → leadForm re-snapshot atomically (rowService.ts).
  const qc = useQueryClient();
  const reassignMut = useMutation({
    mutationFn: (leadFormId: string) =>
      patchRow(rolloutId, row.id, { leadFormId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'rows'] });
      qc.invalidateQueries({ queryKey: ['rollout', rolloutId, 'row', row.id] });
    },
  });
  const adCopy: MetaAdCopy | null = row.metaAdCopy;
  const bodyCopy = renderRowTemplateArray(adCopy?.bodyCopy ?? null, renderCtx);
  const headlines = renderRowTemplateArray(adCopy?.headlines ?? null, renderCtx);
  const descriptions = renderRowTemplateArray(
    adCopy?.descriptions ?? null,
    renderCtx,
  );
  const cta = adCopy?.ctaButton ?? null;

  // Lead Form gate: same effective ad type resolution as the role grid
  // (Campaign type → row override → location default). Only render the
  // section when the resolved type is lead_gen.
  const adType = effectiveMetaAdType(
    { metaAdType: row.metaAdType ?? null },
    campaign ?? { metaAdCopy: { metaAdType: 'traffic' } },
    locationProfile,
  );
  const showLeadForm = adType === 'lead_gen';

  // Multi Lead Form spec §3.3 + §3.4. The row's frozen snapshot powers the
  // display in all cases; resolveLeadForm tells us whether to show the
  // orphan warning banner. Orphan = the leadFormId no longer exists in the
  // assigned Campaign's library; the row's snapshot is still rendered so
  // downstream stays usable until the AM reassigns from the master grid.
  const resolved = resolveLeadForm(row, campaign);

  return (
    <>
      <BuildViewSection
        id="bv-body-copy"
        title="Body copy"
        badge={
          <SectionBadge
            modified={drift.bodyCopy}
            modifiedTitle="Body copy modified from Campaign"
            variations={bodyCopy}
          />
        }
      >
        <VariationsListInline variations={bodyCopy} />
      </BuildViewSection>

      <BuildViewSection
        id="bv-headlines"
        title="Headlines"
        badge={
          <SectionBadge
            modified={drift.headlines}
            modifiedTitle="Headlines modified from Campaign"
            variations={headlines}
          />
        }
      >
        <VariationsListInline variations={headlines} />
      </BuildViewSection>

      <BuildViewSection
        id="bv-descriptions"
        title="Descriptions"
        badge={
          <SectionBadge
            modified={drift.descriptions}
            modifiedTitle="Descriptions modified from Campaign"
            variations={descriptions}
          />
        }
      >
        <VariationsListInline variations={descriptions} />
      </BuildViewSection>

      <BuildViewSection
        id="bv-cta"
        title="CTA"
        badge={<DriftDot visible={drift.cta} title="CTA modified from Campaign" />}
      >
        {cta ? (
          <CopyableCell
            value={cta}
            ariaLabel="CTA button"
            display={<span className="text-sm">{cta}</span>}
          />
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </BuildViewSection>

      {showLeadForm && (
        <LeadFormSection
          row={row}
          campaign={campaign}
          archived={archived}
          leadForm={row.leadForm}
          orphan={resolved.state === 'orphan'}
          renderCtx={renderCtx}
          modified={drift.leadForm}
          onReassign={(id) => reassignMut.mutate(id)}
          reassignBusy={reassignMut.isPending}
        />
      )}
    </>
  );
}

// ---------- internals ----------

function LeadFormSection({
  row,
  campaign,
  archived,
  leadForm,
  orphan,
  renderCtx,
  modified,
  onReassign,
  reassignBusy,
}: {
  row: AccountCampaign;
  campaign: Campaign | null;
  archived: boolean;
  leadForm: CampaignLeadForm | null;
  orphan: boolean;
  renderCtx: RowRenderContext;
  modified: boolean;
  onReassign: (id: string) => void;
  reassignBusy: boolean;
}) {
  // Multi Lead Form spec §3.4. Reassign affordance is rendered when:
  //   - the Campaign carries more than one form, OR
  //   - the row is orphan (regardless of count — there's always
  //     something else to pick).
  // Hidden when the rollout is archived (no mutations allowed) or
  // when the Campaign has a single form (nothing to switch to).
  const multiForm = (campaign?.campaignLeadForms?.length ?? 0) > 1;
  const showReassign = !archived && (multiForm || orphan);

  if (!leadForm) {
    return (
      <BuildViewSection
        id="bv-lead-form"
        title="Lead form"
        badge={<DriftDot visible={modified} title="Lead form modified from Campaign" />}
      >
        {showReassign && (
          <div className="mb-3 flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
            <span className="flex-1">⚠ No lead form on this row. Pick one to reassign.</span>
            <LeadFormCell
              row={row}
              campaign={campaign}
              disabled={false}
              busy={reassignBusy}
              onChange={onReassign}
              ariaLabel="Reassign lead form"
            />
          </div>
        )}
        {!showReassign && <span className="text-slate-400">—</span>}
      </BuildViewSection>
    );
  }
  const lf = leadForm.fields;
  return (
    <BuildViewSection
      id="bv-lead-form"
      title="Lead form"
      badge={<DriftDot visible={modified} title="Lead form modified from Campaign" />}
    >
      {/* Multi Lead Form spec §3.3 / §3.4: orphan banner sits at the top
          of the section so buyers see the warning before the snapshot
          fields. The snapshot still renders below — it's frozen content,
          the buyer can read it, the AM just needs to reassign before
          launch. */}
      {orphan && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
          <span className="flex-1">
            ⚠ This row's lead form was deleted from the Campaign. The snapshot
            below is still being used; reassign before launch.
          </span>
          {!archived && (
            <LeadFormCell
              row={row}
              campaign={campaign}
              disabled={false}
              busy={reassignBusy}
              onChange={onReassign}
              ariaLabel="Reassign lead form"
            />
          )}
        </div>
      )}
      {!orphan && showReassign && (
        <div className="mb-3 flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <span>This Campaign has {campaign!.campaignLeadForms.length} lead forms.</span>
          <LeadFormCell
            row={row}
            campaign={campaign}
            disabled={false}
            busy={reassignBusy}
            onChange={onReassign}
            ariaLabel="Reassign lead form"
          />
        </div>
      )}
      <dl className="space-y-1">
        <BuildViewField label="Form name">
          <span className="text-sm">{leadForm.name}</span>
        </BuildViewField>
        <BuildViewField label="Form type">
          <FormTypeValue value={leadForm.formType} />
        </BuildViewField>
        <BuildViewField label="Headline">
          <LeadFormFieldValue value={lf.headline} renderCtx={renderCtx} />
        </BuildViewField>
        <BuildViewField label="Description">
          <LeadFormFieldValue value={lf.description} renderCtx={renderCtx} />
        </BuildViewField>
        <BuildViewField label="Thank-you headline">
          <LeadFormFieldValue value={lf.thankYouHeadline} renderCtx={renderCtx} />
        </BuildViewField>
        <BuildViewField label="Thank-you description">
          <LeadFormFieldValue value={lf.thankYouDescription} renderCtx={renderCtx} />
        </BuildViewField>
      </dl>
    </BuildViewSection>
  );
}

function FormTypeValue({ value }: { value: FormType }) {
  return <span className="text-sm">{value}</span>;
}

// Composes the per-section drift dot + Copy-all button so they sit
// neatly together in the section header's badge slot. Used by the
// variation sections (Body copy / Headlines / Descriptions). Empty
// variations → no Copy-all (renders only the drift dot when applicable).
export function SectionBadge({
  modified,
  modifiedTitle,
  variations,
}: {
  modified: boolean;
  modifiedTitle: string;
  variations: string[];
}) {
  return (
    <>
      <DriftDot visible={modified} title={modifiedTitle} />
      {variations.length > 0 && <CopyAllButton variations={variations} />}
    </>
  );
}

// LeadFormText is a discriminated union: Generic (no text) vs Custom
// (authored string). Generic renders a muted "Generic" label; Custom
// renders the resolved string + a copy button.
function LeadFormFieldValue({
  value,
  renderCtx,
}: {
  value: LeadFormText;
  renderCtx: RowRenderContext;
}) {
  if (value.mode === 'Generic') {
    return <span className="text-slate-500 italic">Generic</span>;
  }
  const resolved = renderRowTemplate(value.text, renderCtx);
  if (!resolved) {
    return <span className="text-slate-400">—</span>;
  }
  return (
    <CopyableCell
      value={resolved}
      ariaLabel="Lead form field"
      display={<span className="text-sm">{resolved}</span>}
    />
  );
}
